import {
    type DmApis,
    type Extension,
    type ResourceLoaderDefinition,
    type DmStore,
    type ModelsInitializationType,
    type CreateExtensionArgument,
    store as dmStore,
    createFragmentStore,
    PAGE_FRAGMENT_TYPE,
    type Store
} from '@wix/document-manager-core'
import type {Pointer, RMPage, RMTopology} from '@wix/document-services-types'
import {Deferred} from '@wix/document-manager-utils'
import _ from 'lodash'
import * as santaDataFixers from '@wix/santa-data-fixer'
import {addPageJson} from '../utils/addPageJson'
import type {CSaveApi} from './csave/continuousSave'
import type {FixersExtensionAPI} from './fixers/fixers'
import {SNAPSHOTS, RESOURCE_TYPES} from '../constants/constants'
import {
    defaultPageMigrations,
    defaultRunOncePerSite,
    migratorsVersioningConfig
} from './fixers/dataMigration/dataMigrationRunner'

interface PageExistedInRevision {
    rmPage: RMPage
    pageId: string
    title: string
    pageRemovedInTx?: boolean
}
interface PageAddedInTx {
    pageId: string
    title: string
    pageAddedInTx: true
}
type AvailablePageResources = PageExistedInRevision | PageAddedInTx

const INTERACTIONS = {
    FETCH_PAGES: 'fetch-pages',
    RUN_FIXERS: 'ds-run-fixers'
}

const {createStore} = dmStore

const LOADED = 'loaded'
const FAILED = 'failed'
type CompletedRequestStatus = 'loaded' | 'failed'

const createExtension = ({environmentContext, logger}: CreateExtensionArgument): Extension => {
    const pageLoadDefinitions: Record<string, AvailablePageResources> = {}
    let pageTopology: RMTopology[]
    const inProgress: Record<string, Deferred<string>> = {}
    const completed: Record<string, CompletedRequestStatus> = {}
    const fetchResultStatus: Record<string, CompletedRequestStatus> = {}
    let allPagesLoaded: boolean = false

    async function interaction<T>(name: string, fn: (...args: any[]) => Promise<T>): Promise<T> {
        logger.interactionStarted(name)
        const returnValue = await fn()
        logger.interactionEnded(name)

        return returnValue
    }

    const fetchPages = async (pageIds: string[]) => {
        const pageList = {
            //Note that we still load pages that were removed in tx, because we are still not on createRevision and need this for our delta
            //when in createRevision 100%, we probably shouldnt even load them
            pages: pageIds
                .map(pageId => pageLoadDefinitions[pageId])
                .filter(availablePage => {
                    return !('pageAddedInTx' in availablePage)
                })
                .map(availablePage => (availablePage as PageExistedInRevision).rmPage),
            topology: pageTopology
        }
        const resolvedPagesPromise = interaction(INTERACTIONS.FETCH_PAGES, () =>
            environmentContext.fetchPagesFacade!.fetchPages(environmentContext.fetchFn, pageList, 100)
        )

        let result
        let resultStatus: CompletedRequestStatus
        try {
            result = await resolvedPagesPromise
            resultStatus = LOADED
        } catch (e) {
            resultStatus = FAILED
            throw e
        } finally {
            pageIds.forEach(pageId => {
                fetchResultStatus[pageId] = resultStatus
            })
        }

        return result
    }

    const legacy_hack_remove_page_data_items_from_pageJson = (pageJson: any, pageId: string) => {
        // this hack is because the root page node item can only exist once. The server persisted it in both the page and masterPage,
        // but it should only be in the "masterPage" json as far as we are concerned
        // the server recently fixed this bug but it still exists in most page jsons
        // until now it sort of worked "by accident" in mainInitialization, because fetchPages would put the masterPage request last in the array
        // so the masterPage node would overwrite the page node. I prefer we are explicit and that our code reflects the business decision

        if (pageId !== 'masterPage') {
            _.unset(pageJson, ['data', 'document_data', pageId])
            if (pageJson.translations) {
                _.forEach(pageJson.translations, (dataMaps, lang) => {
                    _.unset(pageJson.translations, [lang, 'data', 'document_data', pageId])
                })
            }
        }
    }

    const legacy_hack_keep_masterPage_last = (pageIds: string[]) => {
        //similar to above hack - the references of the page items may be duplicated. masterPage should win.
        //unlike the above hack, this hack can probably be removed at some point, since it may be possible that the items in the page should in fact win
        //but keeping this here for backwards compatibility with previous code
        const masterPageIndex = pageIds.indexOf('masterPage')
        if (masterPageIndex !== -1) {
            pageIds.splice(masterPageIndex, 1)
            pageIds.push('masterPage')
        }
    }

    const setPagesToDal = async (dmAPIs: DmApis, pages: Record<string, any>) => {
        let changes: Store = createStore()
        const addFunc = (pointer: Pointer, value: any) => {
            changes.set(pointer, value)
        }
        _.forOwn(pages, (pageJson: any, pageId: string) => {
            legacy_hack_remove_page_data_items_from_pageJson(pageJson, pageId)
            addPageJson(pageId, pageJson, addFunc)
        })
        if (dmAPIs.coreConfig.experimentInstance.isOpen('dm_fragments')) {
            const changesAsFragmentStore = createFragmentStore()
            changesAsFragmentStore.setFragmentStore(PAGE_FRAGMENT_TYPE, changes)
            changes = changesAsFragmentStore
        }
        dmAPIs.dal.mergeToApprovedStore(changes, 'add-initial-pages')
        dmAPIs.dal.takeSnapshot(SNAPSHOTS.DAL_INITIAL)
    }

    const loadDataFromTransactions = async (dmAPIs: DmApis, pageIds?: string[]) => {
        const {continuousSave} = dmAPIs.extensionAPI as CSaveApi

        //some configurations do not have csave extension, i.e. only read data from statics
        if (continuousSave?.initCSave) {
            //pageIds = undefined => we are loading all pages, including new ones added in txs
            await continuousSave.initCSave(pageIds)
        }
    }
    const load = async (dmAPIs: DmApis, pageIds: string[], isLoadingAllPages = false) => {
        const {experimentInstance} = dmAPIs.coreConfig
        const {dal} = dmAPIs
        const missingPages = pageIds.filter(pageId => !pageLoadDefinitions[pageId])
        if (missingPages.length) {
            throw new Error(`pageResourceLoader: no pages with ids [${missingPages}] available to load`)
        }

        if (allPagesLoaded) {
            return
        }

        const failedPages = pageIds.filter(pageId => fetchResultStatus[pageId] === FAILED)

        if (failedPages.length) {
            throw new Error(`pageResourceLoader: [${pageIds}] already failed to load`)
        }

        const [pagesAlreadyInProgress, pagesToLoad] = _.partition(pageIds, pageId => inProgress[pageId])
        const existingPromises = pagesAlreadyInProgress.map(pageId => inProgress[pageId].promise)

        if (!completed.masterPage && !inProgress.masterPage && !pagesToLoad.includes('masterPage')) {
            pagesToLoad.push('masterPage')
        }
        legacy_hack_keep_masterPage_last(pagesToLoad)

        pagesToLoad.forEach(pageId => {
            inProgress[pageId] = new Deferred()
        })

        if (pagesToLoad.length) {
            const result = await fetchPages(pagesToLoad)

            await setPagesToDal(dmAPIs, result)
            const pageIdsToLoadFromTxs = isLoadingAllPages ? undefined : pagesToLoad
            await loadDataFromTransactions(dmAPIs, pageIdsToLoadFromTxs)
            const {fixers} = dmAPIs.extensionAPI as FixersExtensionAPI
            const wasPageRemovedInATransaction = (pageId: string) =>
                Boolean((pageLoadDefinitions[pageId] as PageExistedInRevision)?.pageRemovedInTx)

            if (isLoadingAllPages && experimentInstance.isOpen('dm_moveValidationBaseline')) {
                dal.createValidationBaseline()
            }
            const santaDataFixersSettings = {
                santaDataFixerPlugins: santaDataFixers.getPlugins(),
                santaDataFixersVersioningConfig: santaDataFixers.getFixerVersioningConfig()
            }
            const migratorsSettings = {
                pageMigrators: defaultPageMigrations,
                siteMigrators: defaultRunOncePerSite,
                migratorsVersioningConfig
            }
            const pagesToFixWhichWereNotRequested = isLoadingAllPages
                ? _(pageLoadDefinitions).pickBy({pageAddedInTx: true}).keys().value()
                : []

            logger.interactionStarted(INTERACTIONS.RUN_FIXERS)
            for (const pageId of pagesToFixWhichWereNotRequested) {
                await fixers.fixPage(pageId, santaDataFixersSettings, migratorsSettings)
            }
            for (const pageId of pagesToLoad) {
                const status = fetchResultStatus[pageId]
                if (status === LOADED) {
                    if (!wasPageRemovedInATransaction(pageId)) {
                        await fixers.fixPage(pageId, santaDataFixersSettings, migratorsSettings)
                    }
                    inProgress[pageId].resolve(status)
                } else {
                    inProgress[pageId].reject(status)
                }
                delete inProgress[pageId]
                completed[pageId] = status
            }
        }

        await Promise.all(existingPromises)

        const notYetLoaded = _.xor(Object.keys(pageLoadDefinitions), Object.keys(completed))
        if (!notYetLoaded.length || isLoadingAllPages) {
            const {fixers} = dmAPIs.extensionAPI as FixersExtensionAPI
            fixers.runSiteMigrators(defaultRunOncePerSite, migratorsVersioningConfig)
            allPagesLoaded = true
        }
        logger.interactionEnded(INTERACTIONS.RUN_FIXERS)
    }

    const loadAll = async (dmAPIs: DmApis) => {
        const pageIds = Object.keys(pageLoadDefinitions)
        return load(dmAPIs, pageIds, true)
    }

    const initAvailableResources = async (dmAPIs: DmApis) => {
        const {continuousSave} = dmAPIs.extensionAPI as CSaveApi
        const pagesAddedInRevision = await continuousSave.getPagesAddedInRevision()
        const pagesRemovedInRevision = await continuousSave.getPagesRemovedInRevision()
        _.forEach(pagesAddedInRevision, ({id, title}) => {
            pageLoadDefinitions[id] = {
                pageId: id,
                title,
                pageAddedInTx: true
            }
        })

        _.forEach(pagesRemovedInRevision, ({id}) => {
            ;(pageLoadDefinitions[id] as PageExistedInRevision).pageRemovedInTx = true
        })
    }

    const getAvailableResources = () => Object.keys(pageLoadDefinitions)

    const createResourceLoader = (dmAPIs: DmApis): ResourceLoaderDefinition[] => {
        return [
            {
                resourceType: RESOURCE_TYPES.PAGE,
                load: (pageIds: string[]) => load(dmAPIs, pageIds, false),
                loadAll: () => loadAll(dmAPIs),
                getAvailableResources,
                initAvailableResources: () => initAvailableResources(dmAPIs)
            }
        ]
    }

    const initializeModels = (initialStore: DmStore, initialModels: ModelsInitializationType) => {
        const {pageList} = initialModels.rendererModel
        const {masterPageJsonFileName, /*mainPageId,*/ pages, topology} = pageList
        pageTopology = topology
        pages.forEach(({pageId, pageJsonFileName, pageUriSEO, title}: any) => {
            pageLoadDefinitions[pageId] = {pageId, title, rmPage: {pageId, pageJsonFileName, title, pageUriSEO}}
        })
        pageLoadDefinitions.masterPage = {
            title: 'masterPage',
            pageId: 'masterPage',
            rmPage: {title: 'masterPage', pageId: 'masterPage', pageJsonFileName: masterPageJsonFileName}
        }
    }
    return {
        name: 'pageResourceLoader',
        createResourceLoader,
        initializeModels
    }
}

export {createExtension}
